// /scada/emsbox — communication & data quality per station (R4). Shows EMS BOX
// online state, last packet, VPN, local buffer, data quality + expandable
// diagnostics (versions, CPU/RAM/disk, LTE). Mock, derived from fleet telemetry.
import { useState } from 'react';
import ScadaLayout from '@/components/feature/ScadaLayout';
import { SectionCard, QualityDot } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useFleetTelemetry } from '@/scada/useTelemetry';
import { useAllowedStationIds } from '@/scada/useScopedStations';

const nameOf = (id: string) => STATIONS.find((s) => s.id === id)?.name ?? id;
const hash = (s: string) => s.split('').reduce((a, c) => a + c.charCodeAt(0), 0);

export default function ScadaEmsBoxPage() {
  const { data: rawData, updatedAt } = useFleetTelemetry();
  const allowed = useAllowedStationIds();
  const data = allowed ? rawData.filter((d) => allowed.has(d.stationId)) : rawData;
  const [open, setOpen] = useState<string | null>(null);

  const online = data.filter((d) => d.comms.emsBoxOnline).length;
  const offline = data.length - online;

  const dotIcon = (ok: boolean, icon: string, title: string) =>
    <i className={icon} title={title} style={{ color: ok ? scada.state.ok : scada.state.alarm, fontSize: 15 }} />;

  return (
    <ScadaLayout active="emsbox">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ marginBottom: 16 }}>
        {[
          { k: 'EMS BOX онлайн', v: `${online}/${data.length}`, c: offline ? scada.state.warning : scada.state.ok },
          { k: 'Втрачено звʼязок', v: String(offline), c: offline ? scada.state.alarm : scada.textDim },
          { k: 'Захищені канали', v: `${data.filter((d) => d.comms.vpnOnline).length}/${data.length}`, c: scada.textDim },
          { k: 'Оновлено', v: updatedAt.toLocaleTimeString('uk-UA'), c: scada.textDim },
        ].map((x) => (
          <div key={x.k} style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ color: scada.textMuted, fontSize: 11 }}>{x.k}</div>
            <div style={{ color: x.c, fontSize: 18, fontWeight: 700, marginTop: 2 }}>{x.v}</div>
          </div>
        ))}
      </div>

      <SectionCard title="Звʼязок та якість даних по станціях" subtitle="Дає змогу відрізнити реальну аварію обладнання від втрати звʼязку" padded={false}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
            <thead>
              <tr style={{ background: scada.panel2 }}>
                {['Станція', 'EMS BOX', 'Останній пакет', 'Канали', 'Буфер', 'Якість даних', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '8px 12px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((d) => {
                const h = hash(d.stationId);
                const diag = { cpu: 12 + (h % 30), ram: 30 + (h % 40), disk: 20 + (h % 25), lte: -(60 + (h % 30)), agent: '1.4.2', cfg: `v${3 + (h % 3)}` };
                const isOpen = open === d.stationId;
                return (
                  <>
                    <tr key={d.stationId} style={{ borderTop: `1px solid ${scada.border}` }}>
                      <td style={{ padding: '10px 12px', color: scada.text, fontSize: 13, fontWeight: 600 }}>{nameOf(d.stationId)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ color: d.comms.emsBoxOnline ? scada.state.ok : scada.state.alarm, fontSize: 12, fontWeight: 600 }}>
                          {d.comms.emsBoxOnline ? 'Онлайн' : 'Офлайн'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: scada.textDim, fontSize: 12 }}>
                        {d.comms.emsBoxOnline ? `${d.comms.lastPacketSec} с тому` : `${Math.round(d.comms.lastPacketSec / 60)} хв тому`}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span className="inline-flex gap-1.5">
                          {dotIcon(d.comms.cloudOnline, 'ri-cloud-line', 'Хмара')}
                          {dotIcon(d.comms.emsBoxOnline, 'ri-router-line', 'EMS BOX')}
                          {dotIcon(d.comms.vpnOnline, 'ri-shield-keyhole-line', 'Захищений канал')}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>
                        <span style={{ color: d.comms.bufferState === 'filling' ? scada.state.warning : scada.textDim }}>
                          {d.comms.bufferState === 'filling' ? 'накопичує' : d.comms.bufferState === 'flushing' ? 'вивантажує' : 'порожній'}
                        </span>
                        <span style={{ color: scada.textMuted }}> · {d.comms.bufferHours} год</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}><QualityDot quality={d.quality} label /></td>
                      <td style={{ padding: '10px 12px' }}>
                        <button onClick={() => setOpen(isOpen ? null : d.stationId)} style={{ background: 'transparent', border: 'none', color: scada.accent, fontSize: 12, cursor: 'pointer' }}>
                          {isOpen ? 'Згорнути' : 'Діагностика'} <i className={isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr style={{ background: scada.bg }}>
                        <td colSpan={7} style={{ padding: '10px 16px' }}>
                          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                            {[
                              { k: 'Версія ПЗ', v: diag.agent },
                              { k: 'Версія конфігурації', v: diag.cfg },
                              { k: 'CPU', v: `${diag.cpu}%` },
                              { k: 'RAM', v: `${diag.ram}%` },
                              { k: 'Диск', v: `${diag.disk}%` },
                              { k: 'Сигнал LTE', v: d.comms.emsBoxOnline ? `${diag.lte} dBm` : '—' },
                            ].map((m) => (
                              <div key={m.k}>
                                <div style={{ color: scada.textMuted, fontSize: 10 }}>{m.k}</div>
                                <div style={{ color: scada.textDim, fontSize: 13, fontWeight: 600 }}>{m.v}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </ScadaLayout>
  );
}
